import React, { FC, ReactNode } from 'react';
import styled, { css } from 'styled-components';

const Table = styled.div<{
  keyWidth?: string;
  maxKeyWidth?: string;
  valueWidth?: string;
}>`
  display: grid;
  grid-template-columns: max-content auto;
  gap: 5px 10px;

  ${({ keyWidth, maxKeyWidth }) =>
    keyWidth
      ? css`
          b {
            min-width: ${keyWidth};

            ${maxKeyWidth ? `max-width: ${maxKeyWidth};` : ''}
            word-break: break-word;
          }
        `
      : ''}

  ${({ valueWidth }) =>
    valueWidth
      ? css`
          span {
            min-width: ${valueWidth};
          }
        `
      : ''}
`;

type KeyAlign = 'middle' | 'top' | 'bottom';
const Key = styled.b<{ verticalAlign?: KeyAlign; disabled?: boolean }>`
  font: inherit;
  font-weight: 500;
  text-align: start;
  align-self: ${({ verticalAlign }) => {
    switch (verticalAlign) {
      case 'bottom':
        return 'flex-end';
      case 'top':
        return 'flex-start';

      case 'middle':
      default:
        return 'center';
    }
  }};
  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.4;
    `}
`;

const Value = styled.span<{ disabled?: boolean }>`
  font: inherit;
  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.4;
    `}
`;

const Header = styled.h3`
  margin: 0;
  padding: 0;
  margin-bottom: 0.2em;
  font-size: 1.2em;
  grid-column: span 2;
`;

export interface KeyListRow {
  key?: ReactNode;
  label?: string;
  value?: ReactNode;
  /** defaults to 'middle' */
  keyAlign?: KeyAlign;
  disabled?: boolean;
  betweenRows?: React.ReactNode;
  id?: any;
}

interface Props {
  rows: KeyListRow[];
  colonKey?: boolean;
  className?: string;
  /** css string such as '1px' */
  keyWidth?: string;
  maxKeyWidth?: string;
  /** css string such as '1px' */
  valueWidth?: string;
  title?: string;
}

const KeyValueList: FC<Props> = ({
  rows,
  colonKey,
  className,
  keyWidth,
  maxKeyWidth,
  valueWidth,
  title,
}) => (
  <Table
    className={className}
    keyWidth={keyWidth}
    maxKeyWidth={maxKeyWidth}
    valueWidth={valueWidth}
  >
    {title && <Header>{title}</Header>}
    {rows.map((row, index) => (
      <React.Fragment key={String(row.id ?? row.key ?? row.label ?? index)}>
        <Key disabled={row.disabled} verticalAlign={row.keyAlign}>
          {row.key}
          {colonKey ? ':' : ''}
        </Key>
        <Value disabled={row.disabled}>{row.value}</Value>
      </React.Fragment>
    ))}
  </Table>
);

export default KeyValueList;
